import { useState } from "react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertCircle, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/\S+@\S+\.\S+/.test(form.email) || !form.subject.trim() || form.message.trim().length < 10) {
      setStatus("invalid");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  const fieldCls =
    "rounded-lg border-input bg-white transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-primary";

  return (
    <form onSubmit={onSubmit} className="space-y-5" noValidate data-testid="contact-form">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="contact-name">{t.contact.name}</Label>
          <Input
            id="contact-name"
            name="name"
            value={form.name}
            onChange={onChange}
            required
            autoComplete="name"
            className={fieldCls}
            data-testid="contact-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-email">{t.contact.email}</Label>
          <Input
            id="contact-email"
            name="email"
            type="email"
            dir="ltr"
            value={form.email}
            onChange={onChange}
            required
            autoComplete="email"
            className={`${fieldCls} text-start`}
            data-testid="contact-email-input"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-subject">{t.contact.subject}</Label>
        <Input
          id="contact-subject"
          name="subject"
          value={form.subject}
          onChange={onChange}
          required
          className={fieldCls}
          data-testid="contact-subject-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-message">{t.contact.message}</Label>
        <Textarea
          id="contact-message"
          name="message"
          value={form.message}
          onChange={onChange}
          required
          rows={6}
          className={fieldCls}
          data-testid="contact-message-input"
        />
      </div>

      {status === "invalid" && (
        <p role="alert" className="text-sm font-semibold text-destructive" data-testid="contact-validation-error">
          {t.contact.validation}
        </p>
      )}
      {status === "success" && (
        <div
          role="status"
          className="rounded-lg border border-primary/25 bg-accent p-4"
          data-testid="contact-success-message"
        >
          <p className="flex items-center gap-2 text-sm font-bold text-primary">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            {t.contact.successAr}
          </p>
          <p className="mt-1 text-sm text-primary/80" dir="ltr">
            {t.contact.successEn}
          </p>
        </div>
      )}
      {status === "error" && (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4" data-testid="contact-error-message">
          <p className="flex items-center gap-2 text-sm font-bold text-destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            {t.contact.errorAr}
          </p>
          <p className="mt-1 text-sm text-destructive/80" dir="ltr">
            {t.contact.errorEn}
          </p>
        </div>
      )}

      <Button
        type="submit"
        disabled={status === "sending"}
        data-testid="contact-submit-button"
        className="rounded-full px-8 py-6 text-base font-bold shadow-md transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 hover:shadow-lg"
      >
        <Send className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />
        {status === "sending" ? t.contact.sending : t.contact.send}
      </Button>
    </form>
  );
};
